// Visual Flow Builder Canvas Page (PRD §1.9, WP-08).
// Fullscreen node-based drag-and-drop flow builder with 17 node types, trigger inspector, graph validator, and publish pipeline.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  type Connection,
  type Edge,
  type Node,
} from '@xyflow/react';
import { useI18n } from '../lib/i18n';
import { useAccounts } from '../lib/accounts';
import { useToast } from '../components/ui/Toast';
import {
  type FlowType,
  type FlowTrigger,
  type AutomationActions,
  type DataIntegrations,
  type FlowNodeType,
  type FlowValidationIssue,
  flowGet,
  flowSave,
  flowPublish,
  flowValidate,
  createDefaultNodeData,
  validateFlowGraph,
  exportFlowJson,
} from '../lib/flows';
import FlowHeader from '../components/flow/FlowHeader';
import NodePalette from '../components/flow/NodePalette';
import FlowCanvasContent from '../components/flow/FlowCanvas';
import NodeConfigPanel from '../components/flow/NodeConfigPanel';
import StartNodeConfigModal, { type StartNodeConfigData } from '../components/flow/StartNodeConfigModal';

export default function FlowBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { selected, numbers } = useAccounts();
  const toast = useToast();

  const isNew = !id || id === 'new';

  // Flow Metadata
  const [flowId, setFlowId] = useState<string | null>(isNew ? null : id);
  const [name, setName] = useState(t('flow.builder.defaultFlowName'));
  const [type, setType] = useState<FlowType>('keyword');
  const [integrationId, setIntegrationId] = useState<string | null>(
    selected?.id || (numbers[0]?.id ?? null)
  );
  const [folderId, setFolderId] = useState<string | null>(null);
  const [trigger, setTrigger] = useState<FlowTrigger | undefined>({
    keywords: ['hello'],
    matchType: 'exact',
  });
  const [automationActions, setAutomationActions] = useState<AutomationActions | undefined>({});
  const [dataIntegrations, setDataIntegrations] = useState<DataIntegrations | undefined>({});
  const [version, setVersion] = useState(1);
  const [published, setPublished] = useState(false);

  // React Flow State
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // UI State
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [validationIssues, setValidationIssues] = useState<FlowValidationIssue[]>([]);

  // Load existing flow
  useEffect(() => {
    if (isNew) {
      // Initialize fresh flow with a starter message node
      const starterId = `node_${Date.now()}`;
      setNodes([
        {
          id: starterId,
          type: 'customNode',
          position: { x: 250, y: 150 },
          data: {
            nodeType: 'text',
            data: { text: 'Hello! How can we assist you today?' },
          },
        },
      ]);
      setLoading(false);
      return;
    }

    let active = true;
    async function load() {
      setLoading(true);
      try {
        const res = await flowGet({ id: id! });
        if (!active) return;
        if (res.data.ok && res.data.flow) {
          const flow = res.data.flow;
          setFlowId(flow.id);
          setName(flow.name);
          setType(flow.type);
          setIntegrationId(flow.integrationId || null);
          setFolderId(flow.folderId || null);
          setTrigger(flow.trigger);
          setAutomationActions(flow.automationActions);
          setDataIntegrations(flow.dataIntegrations);
          setVersion(flow.version || 1);
          setPublished(!!flow.published);

          // Map stored nodes to ReactFlow nodes
          const mappedNodes: Node[] = (flow.nodes || []).map((n) => ({
            id: n.id,
            type: 'customNode',
            position: n.position || { x: 100, y: 100 },
            data: {
              nodeType: n.type,
              data: n.data || {},
            },
          }));

          // Map stored edges to ReactFlow edges
          const mappedEdges: Edge[] = (flow.edges || []).map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle,
            targetHandle: e.targetHandle,
            label: e.sourceHandle === 'true' ? 'YES' : e.sourceHandle === 'false' ? 'NO' : undefined,
            style:
              e.sourceHandle === 'true'
                ? { stroke: '#10b981', strokeWidth: 2 }
                : e.sourceHandle === 'false'
                ? { stroke: '#ef4444', strokeWidth: 2 }
                : { stroke: '#0284c7', strokeWidth: 2 },
          }));

          setNodes(mappedNodes);
          setEdges(mappedEdges);
        } else {
          toast.error(t('flow.builder.loadFailed'));
          navigate('/flows');
        }
      } catch (err: any) {
        toast.error(err.message || t('flow.builder.loadFailed'));
        navigate('/flows');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [id, isNew]);

  // Client-side real-time graph validation
  useEffect(() => {
    const issues = validateFlowGraph(name, type, nodes, edges, trigger);
    setValidationIssues(issues);
  }, [name, type, nodes, edges, trigger]);

  // Edge Connection Handler
  const handleConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) => {
        const isTrue = connection.sourceHandle === 'true';
        const isFalse = connection.sourceHandle === 'false';
        const customEdge: Edge = {
          id: `e_${connection.source}_${connection.sourceHandle || ''}_${connection.target}`,
          source: connection.source,
          target: connection.target,
          sourceHandle: connection.sourceHandle,
          targetHandle: connection.targetHandle,
          label: isTrue ? 'YES' : isFalse ? 'NO' : undefined,
          style: isTrue
            ? { stroke: '#10b981', strokeWidth: 2 }
            : isFalse
            ? { stroke: '#ef4444', strokeWidth: 2 }
            : { stroke: '#0284c7', strokeWidth: 2 },
        };
        return addEdge(customEdge, eds);
      });
    },
    [setEdges]
  );

  // Add Node from Palette or Drop
  const handleAddNode = useCallback(
    (nodeType: FlowNodeType, position?: { x: number; y: number }) => {
      const newNodeId = `node_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const pos = position || {
        x: 250 + Math.random() * 80,
        y: 150 + Math.random() * 80,
      };

      const newNode: Node = {
        id: newNodeId,
        type: 'customNode',
        position: pos,
        data: {
          nodeType,
          data: createDefaultNodeData(nodeType),
        },
      };

      setNodes((nds) => [...nds, newNode]);
      setSelectedNodeId(newNodeId);
    },
    [setNodes]
  );

  // Delete Node Handler
  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(null);
      }
    },
    [selectedNodeId, setNodes, setEdges]
  );

  // Update Node Data Handler (called from NodeConfigPanel)
  const handleUpdateNodeData = useCallback(
    (nodeId: string, updatedData: any) => {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === nodeId) {
            return {
              ...n,
              data: {
                ...n.data,
                data: updatedData,
              },
            };
          }
          return n;
        })
      );
    },
    [setNodes]
  );

  // Save Flow Draft
  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const payloadNodes = nodes.map((n) => ({
        id: n.id,
        type: (n.data as any)?.nodeType as FlowNodeType,
        position: n.position,
        data: (n.data as any)?.data || {},
      }));

      const payloadEdges = edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle || undefined,
        targetHandle: e.targetHandle || undefined,
      }));

      const res = await flowSave({
        id: flowId || undefined,
        name,
        type,
        integrationId: integrationId || null,
        folderId: folderId || null,
        trigger,
        automationActions,
        dataIntegrations,
        nodes: payloadNodes,
        edges: payloadEdges,
      });

      if (res.data.ok) {
        toast.success(t('flow.save.success'));
        setFlowId(res.data.id);
        setVersion(res.data.version);
        if (isNew) {
          navigate(`/flows/${res.data.id}`, { replace: true });
        }
      } else {
        toast.error('Failed to save flow');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to save flow');
    } finally {
      setSaving(false);
    }
  };

  // Publish Flow
  const handlePublish = async () => {
    // First run server-side validate
    setPublishing(true);
    try {
      const payloadNodes = nodes.map((n) => ({
        id: n.id,
        type: (n.data as any)?.nodeType as FlowNodeType,
        position: n.position,
        data: (n.data as any)?.data || {},
      }));

      const payloadEdges = edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle || undefined,
        targetHandle: e.targetHandle || undefined,
      }));

      const valRes = await flowValidate({
        nodes: payloadNodes,
        edges: payloadEdges,
      });

      if (!valRes.data.valid) {
        toast.error(t('flow.publish.validationFailed'));
        setValidationIssues(
          (valRes.data.errors || []).map((errStr: string) => ({
            level: 'error' as const,
            message: errStr,
          }))
        );
        setPublishing(false);
        return;
      }

      // If we don't have a flowId saved yet, save first
      let activeFlowId = flowId;
      if (!activeFlowId) {
        const saveRes = await flowSave({
          name,
          type,
          integrationId: integrationId || null,
          folderId: folderId || null,
          trigger,
          automationActions,
          dataIntegrations,
          nodes: payloadNodes,
          edges: payloadEdges,
        });
        if (!saveRes.data.ok) {
          toast.error('Failed to save before publishing');
          setPublishing(false);
          return;
        }
        activeFlowId = saveRes.data.id;
        setFlowId(activeFlowId);
        setVersion(saveRes.data.version);
      }

      // Call flowPublish
      const pubRes = await flowPublish({ id: activeFlowId });
      if (pubRes.data.ok) {
        toast.success(t('flow.publish.success'));
        setPublished(true);
        setVersion(pubRes.data.version);
        if (isNew) {
          navigate(`/flows/${activeFlowId}`, { replace: true });
        }
      } else {
        toast.error('Failed to publish flow');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to publish flow');
    } finally {
      setPublishing(false);
    }
  };

  // Export JSON
  const handleExport = () => {
    const payloadNodes = nodes.map((n) => ({
      id: n.id,
      type: (n.data as any)?.nodeType as FlowNodeType,
      position: n.position,
      data: (n.data as any)?.data || {},
    }));

    const payloadEdges = edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle || undefined,
      targetHandle: e.targetHandle || undefined,
    }));

    exportFlowJson({
      name,
      type,
      integrationId,
      trigger,
      automationActions,
      dataIntegrations,
      nodes: payloadNodes,
      edges: payloadEdges,
    });
    toast.success(t('flow.export.success'));
  };

  // Currently selected node object
  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null;
    return nodes.find((n) => n.id === selectedNodeId) || null;
  }, [selectedNodeId, nodes]);

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-surface dark:bg-slate-900">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-surface dark:bg-slate-900">
      {/* Top Header Toolbar */}
      <FlowHeader
        name={name}
        type={type}
        version={version}
        published={published}
        validationIssues={validationIssues}
        saving={saving}
        publishing={publishing}
        onNameChange={setName}
        onOpenSettings={() => setSettingsOpen(true)}
        onExport={handleExport}
        onSaveDraft={handleSaveDraft}
        onPublish={handlePublish}
      />

      {/* Main Canvas Workspace */}
      <div className="relative flex flex-1 overflow-hidden">
        {/* Left Node Palette */}
        <NodePalette onAddNode={(t) => handleAddNode(t)} />

        {/* Center React Flow Canvas */}
        <div className="relative flex-1">
          <ReactFlowProvider>
            <FlowCanvasContent
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={handleConnect}
              onNodeSelect={setSelectedNodeId}
              onAddNodeAtPosition={handleAddNode}
            />
          </ReactFlowProvider>
        </div>

        {/* Right Node Inspector / Property Panel */}
        {selectedNode && (
          <NodeConfigPanel
            node={{
              id: selectedNode.id,
              type: (selectedNode.data as any)?.nodeType as FlowNodeType,
              position: selectedNode.position,
              data: (selectedNode.data as any)?.data || {},
            }}
            onUpdate={(id: string, updated: any) => handleUpdateNodeData(id, updated)}
            onDelete={(id: string) => handleDeleteNode(id)}
            onClose={() => setSelectedNodeId(null)}
          />
        )}
      </div>

      {/* Flow Settings & Trigger Modal */}
      <StartNodeConfigModal
        open={settingsOpen}
        initialData={{
          name,
          type,
          integrationId,
          folderId,
          trigger,
          automationActions,
          dataIntegrations,
        }}
        onSave={(data: StartNodeConfigData) => {
          setName(data.name);
          setType(data.type);
          setIntegrationId(data.integrationId || null);
          setFolderId(data.folderId || null);
          setTrigger(data.trigger);
          setAutomationActions(data.automationActions);
          setDataIntegrations(data.dataIntegrations);
          setSettingsOpen(false);
        }}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
}
